import * as React from 'react'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export type PopoverComboboxProps<T> = {
  items: T[]
  value: T | null | undefined
  onValueChange: (value: T | null) => void
  getItemLabel: (item: T) => string
  /** Optional: stable key for each item (defaults to getItemLabel). */
  getItemKey?: (item: T) => string | number
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  /** When true, show a clear button when a value is selected. */
  showClear?: boolean
  id?: string
  className?: string
  triggerClassName?: string
}

/** Popover + cmdk list with client-side filtering (legacy pattern for forms). */
export function PopoverCombobox<T>({
  items,
  value,
  onValueChange,
  getItemLabel,
  getItemKey,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
  disabled,
  showClear = false,
  id,
  className,
  triggerClassName,
}: PopoverComboboxProps<T>) {
  const keyOf = getItemKey ?? getItemLabel
  const [open, setOpen] = React.useState(false)

  const selectedLabel = value != null ? getItemLabel(value) : null
  const selectedKey = value != null ? String(keyOf(value)) : null

  const handleSelect = (item: T) => {
    onValueChange(item)
    setOpen(false)
  }

  const handleClear = (ev: React.MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    onValueChange(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          id={id}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            triggerClassName,
            className
          )}
        >
          <span className={selectedLabel ? undefined : 'text-muted-foreground'}>
            {selectedLabel ?? placeholder}
          </span>
          <span className='flex shrink-0 items-center gap-0.5'>
            {showClear && selectedLabel ? (
              <span
                role='button'
                tabIndex={-1}
                aria-label='Clear'
                onClick={handleClear}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault()
                    handleClear(ev as unknown as React.MouseEvent)
                  }
                }}
                className='rounded p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              >
                <XIcon className='size-4' />
              </span>
            ) : null}
            <ChevronDownIcon className='size-4 shrink-0 opacity-50' />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='w-(--radix-popover-trigger-width) p-0'
        align='start'
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {items.map((item) => {
              const label = getItemLabel(item)
              const itemKey = String(keyOf(item))
              const isSelected =
                selectedKey != null && itemKey === selectedKey
              return (
                <CommandItem
                  key={keyOf(item)}
                  value={itemKey}
                  keywords={[label]}
                  onSelect={() => handleSelect(item)}
                >
                  <span className='flex-1 truncate'>{label}</span>
                  {isSelected ? (
                    <CheckIcon className='size-4 shrink-0' />
                  ) : null}
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
